export const talendEndpoint = process.env.VUE_APP_TALEND_ENDPOINT || '';

/**
 * Host Talend ESB без /services/ префикса — база адресов шлюза proxy-medeo_pimcore-session_me.
 */
export const pretensionsDirectEndpoint = talendEndpoint.replace(/\/services$/, '');

/**
 * Same base as report-constructor / operation reports; override with VUE_APP_WORK_CALC_ENDPOINT.
 * The stands point it at work-calc behind the proxy-medeo_pimcore-session_me gateway (task 12393): the gateway
 * checks the session and lets the browser reach only the four browser paths, work-calc checks the role per path.
 */
export const workCalcEndpoint = process.env.VUE_APP_WORK_CALC_ENDPOINT || talendEndpoint;

/**
 * AccountingSheetsListPage: Talend GetAccountingSheetList — несколько platformId в одном запросе (через запятую).
 * По умолчанию true: один запрос с platformId=id1,id2,...
 * VUE_APP_ACCOUNTING_SHEETS_LIST_TALEND_MULTI_PLATFORM_SINGLE_REQUEST=false — отдельный запрос на платформу + слияние на клиенте (legacy).
 */
export const accountingSheetsListTalendMultiPlatformSingleRequestSupported = process.env.VUE_APP_ACCOUNTING_SHEETS_LIST_TALEND_MULTI_PLATFORM_SINGLE_REQUEST !== 'false';
export const pimCoreUrl = process.env.VUE_APP_PIMCORE_HOST || '';
export const pimCoreAssetFolderId = parseInt(process.env.VUE_APP_PIMCORE_ASSET_FOLDER || '0', 10);
export const apiUrl = process.env.VUE_APP_API_URL || 'http://localhost:1337/api/';

export const folderIds: Record<string, string> = {
  Supplier: process.env.VUE_APP_PIMCORE_FOLDER_SUPPLIER || '',
};

export const graphqlEndpoints = {
  createProvider: `${talendEndpoint}/provider/create`,
  updateProvider: `${talendEndpoint}/provider/update`,
  listings: `${talendEndpoint}/listings`,
  createApplication: `${talendEndpoint}/application/create`,
  processHRApplication: `${talendEndpoint}/applications/process-HR`,
  processHRReworkApplication: `${talendEndpoint}/applications/process-HR-rework`,
  processSupplierApplication: `${talendEndpoint}/applications/process-supplier`,
  processEditApplication: `${talendEndpoint}/applications/process-edit`,
  processCoordinatorApplication: `${talendEndpoint}/applications/process-coordinator`,
  fillApplicationActuals: `${pretensionsDirectEndpoint}/services/esb-connectors/proxy-medeo_pimcore-session_me/p2p-m_vuefront2m_jbpm-fill_application_actuals/services/applications/fill-application-actuals`,
  editFactualData: `${talendEndpoint}/applications/factual-data`,
  getAggregatedOperations: `${talendEndpoint}/application/aggregated-operations`,
  getAggregatedApplications: `${talendEndpoint}/application/aggregated-applications-hr`,
  removeSupplier: `${pretensionsDirectEndpoint}/services/esb-connectors/proxy-medeo_pimcore-session_me/p2p-m_vuefront2m_pimcore-applicat_hr_operation_unpublish/remove-supplier`,
};

export const ratesEndpoints = {
  createRatesTable: `${talendEndpoint}/rates`,
  updateRatesTable: `${talendEndpoint}/rates`,
  deleteRatesTable: `${talendEndpoint}/rates`,
  getRatesTables: `${talendEndpoint}/rates`,
  /**
   * Список видов работ с перекрытием полос (задача 10577). Идёт в work-calc, как
   * report-constructor-data: у ESB-шлюза `/services/rates` своего плеча под этот адрес
   * нет, а данные те же и только на чтение.
   */
  conflicts: `${workCalcEndpoint}/tariff-conflicts`,
};

export const usersApi = {
  gauth: `${apiUrl}users/authenticate-gauth`,
  auth: `${apiUrl}users/authenticate`,
  me: `${apiUrl}users/me`,
  logout: `${apiUrl}users/logout`,
  editInfo: `${apiUrl}users/send-request-edit-info`,
};

/**
 * Formation page: GET work-calc mart for calculated operations (task 7009).
 * Rollback to Talend: set VUE_APP_ACCOUNTING_SHEET_CALCULATED_OPERATIONS_USE_MART=false (same build, Talend URL below).
 * DevOps: new path on work-calc host — `/accounting-sheet/calculated-operations` (timeout/route like report-constructor-data).
 */
export const accountingSheetCalculatedOperationsUseMart = process.env.VUE_APP_ACCOUNTING_SHEET_CALCULATED_OPERATIONS_USE_MART !== 'false';
export const accountingSheetWriteThroughUseWorkCalc = process.env.VUE_APP_ACCOUNTING_SHEET_WRITE_THROUGH_USE_WORK_CALC === 'true';
/**
 * «Сформировать» via the formation starter connector (task 11817), replacing the Talend `CreateAccountingSheet` job:
 * the request carries the screen snapshot, the answer an outcome per supplier. Takes effect only together with
 * {@link accountingSheetCalculatedOperationsUseMart}: the snapshot is checked against the mart, a Talend screen never
 * matches it. Rollback: `false` and rebuild; the Talend DC keeps serving `toShapeAccountingSheet` until it is scaled to 0.
 */
export const accountingSheetFormationUseStarter = process.env.VUE_APP_ACCOUNTING_SHEET_FORMATION_USE_STARTER === 'true';

/**
 * Status operations via the Python ESB connector (task 10925), replacing the Talend
 * `AccountingSheetStatusUtils` job. No Route of its own — published through
 * proxy-medeo_pimcore-session_me, same as {@link accountingSheet.accountingSheetUpdate}.
 * The connector closes five holes the Talend job had open: a signal rejected by jbpm,
 * an unparsable body, an unknown sheet, a sheet with no process behind it and a target
 * status the process has no branch for now answer with an error instead of a green tick.
 * It also writes «кто запросил перевод» into the sheet feed.
 * Rollback: restore the six `${talendEndpoint}/accounting-sheet/status/...` URLs below
 * and rebuild; the Talend DC keeps serving that path until it is scaled to 0.
 * `sign` is NOT part of this job — it lives in AccountingSheetSigning on another route.
 */
const accountingSheetStatusEndpoint = `${pretensionsDirectEndpoint}/services/esb-connectors/proxy-medeo_pimcore-session_me/proxy-m_vuefront2m_jbpm-accounting_sheet_status/services/accounting-sheet/status`;

export const accountingSheet = {
  /**
   * GET work-calc mart: date_from, date_to, supplier, clientIds, statusCodes, stream ({@link reportStream}),
   * optional platform_id.
   */
  calculatedOperationsMart: `${workCalcEndpoint}/accounting-sheet/calculated-operations`,
  calculatedOperations: `${talendEndpoint}/operations/calculate`
    + '?dateFrom={dateFrom}&dateTo={dateTo}&supplier={supplier}&platformId={platformId}&clientIds={clientIds}',
  bulkQualityPerformance: `${talendEndpoint}/operations/bulk/quality-performance`,
  bulkQualityPerformanceWorkCalc: `${workCalcEndpoint}/accounting-sheet/write-through-save`,
  toShapeAccountingSheet: `${talendEndpoint}/accounting-sheet`,
  /**
   * Formation starter (task 11817), called instead of {@link toShapeAccountingSheet} while
   * {@link accountingSheetFormationUseStarter} is on. No Route of its own — published through
   * proxy-medeo_pimcore-session_me, same as {@link formationStatus}; the gateway waits 540 s for it.
   */
  formationStart: `${pretensionsDirectEndpoint}/services/esb-connectors/proxy-medeo_pimcore-session_me/proxy-m_vuefront2m_work_calc2m_pimcore2m_jbpm-create_accounting_sheet/services/accounting-sheet/create`,
  /**
   * Poll JBPM formation status via Python ESB connector (proxy-medeo_pimcore-session_me).
   * Short path /services/accounting-sheet/formation-status has no OpenShift Route on hrtest/prod.
   */
  formationStatus: `${pretensionsDirectEndpoint}/services/esb-connectors/proxy-medeo_pimcore-session_me/p2p-m_jbpm2m_sheets2m_vuefront-formation_status/formation-status`,
  /**
   * Sheet event feed — second leg of the same connector, keyed by sheetId.
   * `markSeen=1` is sent only when the panel is opened: rendering the card must
   * not clear a badge the user has never seen.
   */
  sheetEvents: `${pretensionsDirectEndpoint}/services/esb-connectors/proxy-medeo_pimcore-session_me/p2p-m_jbpm2m_sheets2m_vuefront-formation_status/sheet-events`,
  /**
   * Load the sheet list via Python ESB connector (task 10153), replacing the Talend
   * `get-accounting-sheet-list` job. No Route of its own — published through
   * proxy-medeo_pimcore-session_me, same as {@link accountingSheetSingle} below.
   * The answer changes shape on purpose: `{"totalItems": N, "items": [...]}` instead
   * of a bare array, and an empty result is HTTP 200 with an empty `items` instead
   * of the bodiless 404 the Talend job produced. Readers below accept both shapes,
   * so the URL can be rolled back on its own.
   * The tail is `/sheet-list`, not the Talend `/accounting-sheet/list`: the gateway
   * already prefixes the address with the connector name, so repeating the entity
   * there would only pin a retiring Talend route into a new service.
   * Rollback: restore the `${talendEndpoint}/accounting-sheet/list` URL and rebuild;
   * the Talend DC keeps serving that path until it is scaled to 0.
   */
  accountingSheetList: `${pretensionsDirectEndpoint}/services/esb-connectors/proxy-medeo_pimcore-session_me/proxy-m_vuefront2m_sheets-sheet_list/sheet-list`,
  /**
   * Load the sheet card via Python ESB connector (task 10333), replacing the Talend
   * `get-accounting-sheet-single` job. No Route of its own — published through
   * proxy-medeo_pimcore-session_me, same as {@link accountingSheetUpdate} below.
   * Three answers change on purpose: documents carry `compareResult`, documents with
   * an empty `type` no longer arrive (the filter moved to the backend), and `createdAt`
   * finally shows minutes instead of the month number.
   * Rollback: restore the `${talendEndpoint}` URL and rebuild; the Talend DC keeps
   * serving that path until it is scaled to 0.
   */
  accountingSheetSingle: `${pretensionsDirectEndpoint}/services/esb-connectors/proxy-medeo_pimcore-session_me/proxy-m_vuefront2m_sheets2m_pimcore-sheet_single/services/accounting-sheet/single`,
  /**
   * Save ERP data via Python ESB connector (task 8045), replacing the Talend
   * `update-accounting-sheet` job. The connector has no Route of its own — the fleet
   * publishes browser-facing connectors through proxy-medeo_pimcore-session_me, same as
   * {@link formationStatus} above. Rollback: restore the `${talendEndpoint}` URL below
   * and rebuild; the Talend DC keeps serving that path until it is scaled to 0.
   */
  accountingSheetUpdate: `${pretensionsDirectEndpoint}/services/esb-connectors/proxy-medeo_pimcore-session_me/proxy-m_vuefront2m_sheets-update_accounting_sheet/services/accounting-sheet/update`,
  downloadFiles: {
    linkAccountingSheetWithoutSignature: `${talendEndpoint}/accounting-sheet/link`,
    linkConstructorForDownload: `${talendEndpoint}/files/download?authorization={authorizationToken}&bucket={bucket}&file={file}`,
  },
  status: {
    processControlling: `${accountingSheetStatusEndpoint}/process-controlling`,
    processAccountant: `${accountingSheetStatusEndpoint}/process-accounting`,
    processAccountantPaymentDone: `${accountingSheetStatusEndpoint}/process-payment`,
    cancel: `${accountingSheetStatusEndpoint}/cancel`,
    recall: `${accountingSheetStatusEndpoint}/recall`,
    sign: `${talendEndpoint}/accounting-sheet/sign`,
    change: `${accountingSheetStatusEndpoint}/change`,
    supplierChangesAccept: `${accountingSheetStatusEndpoint}/supplier-changes-accept`,
    supplierChangesReject: `${accountingSheetStatusEndpoint}/supplier-changes-reject`,
    supplierChangesUnblock: `${accountingSheetStatusEndpoint}/supplier-changes-unblock`,
    packageReject: `${accountingSheetStatusEndpoint}/package-reject`,
    packageUnblock: `${accountingSheetStatusEndpoint}/package-unblock`,
    /**
     * Кто решает по изменённому листу поставщика (10857, встреча 16.09.2026):
     * ответ `{"result": {"canDecide": true|false}}`. Список решающих живёт в настройке
     * коннектора статусов — карточка его не знает и только спрашивает право, чтобы
     * показать кнопки неактивными. Отказ на записи всё равно проверяет коннектор.
     */
    supplierChangesPermission: `${accountingSheetStatusEndpoint}/supplier-changes-permission`,
  },
};

/**
 * PHP `pretensions` behind the proxy-medeo_pimcore-session_me gateway (task 12003): the gateway checks
 * the session and passes the user to PHP in its own header, so the role and the author of a change
 * come from the session, not from the browser. The service has no external routes of its own.
 */
const pretensionsGatewayPath = '/services/esb-connectors/proxy-medeo_pimcore-session_me/pretensions';
const pretensionsGateway = `${pretensionsDirectEndpoint}${pretensionsGatewayPath}`;

export const pretension = {
  create: `${talendEndpoint}/pretension`,
  update: `${talendEndpoint}/pretension/update`,
  /**
   * Load the pretension list via Python ESB connector (task 10833), replacing the
   * Talend `GetPretensionList` job. No Route of its own — published through
   * proxy-medeo_pimcore-session_me, same as {@link single} below.
   * The answer stays `{data, total}`; rows also carry `sheet_ids` and `seen` for the
   * session user. A supplier gets only own pretensions whatever the browser sends.
   * Rollback: restore the `${talendEndpoint}/pretension/list` URL and rebuild;
   * the Talend DC keeps serving that path until it is scaled to 0.
   */
  list: `${pretensionsDirectEndpoint}/services/esb-connectors/proxy-medeo_pimcore-session_me/proxy-m_vuefront2m_pretensions-pretension_list/pretension-list`,
  /**
   * Load the pretension card via Python ESB connector (task 11655, stage 5),
   * replacing the Talend `get-pretension-single` job. No Route of its own —
   * published through proxy-medeo_pimcore-session_me, same as
   * {@link accountingSheetSingle} above.
   * The answer does NOT change: codes and body stay exactly what the Talend job
   * returned, because PretensionPage.vue reads both (401/403/404 route a supplier
   * to AccessDenied). What changes is inside the connector — the job resolved the
   * external site name from inside the cluster to reach /api/users/me and
   * /pretension-list, and the DNS outage of 04.09.2026 closed this card.
   * Rollback: restore the `${talendEndpoint}/pretension/single` URL and rebuild;
   * the Talend DC keeps serving that path until it is scaled to 0.
   */
  single: `${pretensionsDirectEndpoint}/services/esb-connectors/proxy-medeo_pimcore-session_me/proxy-m_vuefront2m_pretensions2m_pimcore-pretension_single/services/pretension/single`,
  /**
   * Лента событий претензии для «Истории обработки» (12019): оплаты из 1С и их расхождения.
   * GET `?pretensionId=&markSeen=0|1`, тело как у {@link accountingSheet.sheetEvents}.
   * Поставщику коннектор отвечает 403 — карточка ему панель и не показывает.
   */
  events: `${pretensionsDirectEndpoint}/services/esb-connectors/proxy-medeo_pimcore-session_me/p2p-m_vuefront2m_pretensions-pretension_events/pretension-events`,
  /**
   * Draft pretensions (pretensions-create-preview-draft-spec.md §2.8).
   * Via Talend ESB `draft-pretension` service. update/delete take :id in path.
   * preview returns application/pdf (raw axios responseType:'blob', not Task.execute).
   */
  /**
   * Черновик = статус претензии (Вариант B, draft-as-status spec). CRUD + reset идут
   * в PHP через шлюз, как document-link/start-signing. preview — через Talend (нужно
   * обогащение справочниками).
   */
  draft: {
    save: `${pretensionsGateway}/pretension-save-draft`,
    get: `${pretensionsGateway}/pretension-get-draft`,
    list: `${pretensionsGateway}/pretension-list-drafts`,
    update: `${pretensionsGateway}/pretension-update-draft`,
    delete: `${pretensionsGateway}/pretension-delete-draft`,
    resetToDraft: `${pretensionsGateway}/pretension-reset-to-draft`,
    // ОВ1: точный путь Talend preview-роута подтвердить (enrichment → PHP /pretension-preview-pdf).
    preview: `${talendEndpoint}/pretension/preview`,
  },
  /**
   * Opened by <a href>, which cannot send the Authorization header: the gateway takes the
   * token from the {@link documentLinkTokenCookie} cookie on this GET only.
   */
  documentLink: `${pretensionsGateway}/pretension-document-link`,
  /**
   * T4 (FML-wsj2.3): начало подписания. PHP через шлюз, без Talend-hop'а
   * (см. pretensions-signing-frontend-spec.md §2.2). Контракт:
   * pretensions-signing-php-spec.md §5. POST body: {id, signerEmail, diadocLogin, diadocPassword}.
   * Через Task.execute идти НЕЛЬЗЯ (Task.execute теряет JSON-тело 409) —
   * см. PretensionController.startSigning (raw axios path, R-T4-11).
   */
  startSigning: `${pretensionsGateway}/pretension-start-signing`,
  /**
   * Cancel with a mandatory reason (task 11991): the pretensions service writes status,
   * reason and history in one transaction, then signals jBPM. Only through the
   * proxy-medeo_pimcore-session_me gateway: the role comes from its session header.
   * Replaces the Talend `pretension/status/cancel` path.
   */
  cancel: `${pretensionsGateway}/pretension-cancel`,
  status: {
    processSigner: `${talendEndpoint}/pretension/status/process-signer`,
    paymentDone: `${talendEndpoint}/pretension/status/process-payment-done`,
    processControlling: `${talendEndpoint}/pretension/status/process-controlling`,
    controlResubmit: `${talendEndpoint}/pretension/status/control-resubmit`,
    resumeSigning: `${talendEndpoint}/pretension/status/resume-signing`,
    resumeSupplier: `${talendEndpoint}/pretension/status/resume-supplier`,
    resumeAccounting: `${talendEndpoint}/pretension/status/resume-accounting`,
    sendToErp: `${talendEndpoint}/pretension/status/send-to-erp`,
  },
};

/**
 * Session token for {@link pretension.documentLink}. The gateway reads it only there
 * (points.1 tokenCookiePaths); the path keeps the cookie off every other request.
 * Set with the session in the profile store, removed on logout.
 */
export const documentLinkTokenCookie = {
  name: 'esb_authorization',
  attributes: {
    path: `${pretensionsGatewayPath}/pretension-document-link`,
    secure: true,
    sameSite: 'strict' as const,
  },
};

export const pretensionsReport = {
  /**
   * Same connector as {@link pretension.list} (task 10833); the PHP envelope comes back whole.
   * Rollback: restore the `${talendEndpoint}/pretension/report` URL and rebuild.
   */
  getData: `${pretensionsDirectEndpoint}/services/esb-connectors/proxy-medeo_pimcore-session_me/proxy-m_vuefront2m_pretensions-pretension_list/pretension-report`,
};

/**
 * Diadoc OIDC signer auth (FML-5vmy, Часть 1). Фьюче-флаг DIADOC_AUTH_MODE:
 *   - legacy — текущий диалог логин/пароль Диадока (SigningModal) без изменений;
 *   - oidc — диалог не показываем, авторизация подписанта через OIDC-редирект.
 * Дефолт legacy. Имя/значения согласованы с diadoc-api/Talend.
 * Чтение флага централизовано здесь (не размазано по компонентам) —
 * сервис signerSession.ts его ре-экспортит через isOidcMode().
 */
export type DiadocAuthMode = 'legacy' | 'oidc';
export const diadocAuthMode: DiadocAuthMode = process.env.VUE_APP_DIADOC_AUTH_MODE === 'oidc' ? 'oidc' : 'legacy';

export const diadocAuth = {
  /**
   * Канонический ESB-роут проверки сессии подписанта (vue → Talend → diadoc-api).
   * НЕ ходим в diadoc-api за данными напрямую. Ответ-контракт: { valid, expiresAt }.
   */
  checkSignerSession: `${talendEndpoint}/diadoc/check-signer-session`,
  /**
   * OIDC login — единственное исключение «браузер↔diadoc-api напрямую» (редирект, не AJAX).
   * Дефолт — test stand; для prod переопределяется VUE_APP_DIADOC_OIDC_LOGIN_URL.
   */
  oidcLoginUrl: process.env.VUE_APP_DIADOC_OIDC_LOGIN_URL || 'https://fmlhrtest.kt-team.de/diadoc/auth/oidc/login',
};

export const talendCommon = {
  uploadFileToS3: `${talendEndpoint}/files/upload`,
  downloadFileFromS3: `${talendEndpoint}/files/download?authorization={authorizationToken}&bucket={bucket}&file={file}`,
};

export const processSupplier = {
  confirmFact: `${talendEndpoint}/applications/process-supplier-confirm-fact`,
};

export const allowedRoutes = [
  'ApplicationHR',
];

export const gauthOption = {
  clientId: process.env.VUE_APP_GAUTH_CLIENT_ID || '',
  password: process.env.VUE_APP_GAUTH_PASSWORD || null,
  scope: 'profile email',
  prompt: 'select_account',
};

export const report = {
  generateOperationReport: `${talendEndpoint}/report/operation`,
};

export const reportStream = {
  // Stage 1 contract: frontend sends preferred mode by default.
  mode: process.env.VUE_APP_REPORT_STREAM_MODE || '1',
};

export const reportConstructor = {
  getData: `${workCalcEndpoint}/report-constructor-data`,
};

export const operationReport = {
  // Uses the unified report fact endpoint in work-calc.
  getData: `${workCalcEndpoint}/report-constructor-data`,
};
